import { ApplicationConfig, importProvidersFrom, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection, APP_INITIALIZER, ErrorHandler, inject } from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { routes } from './app.routes';
import { AuthService } from './core/auth.service';
import { JwtInterceptor } from './core/jwt.interceptor';
import { HttpErrorInterceptor } from './core/http-error.interceptor';
import * as Sentry from "@sentry/angular";

export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
  return new (class implements TranslateLoader {
    constructor(private h: HttpClient) {}
    public getTranslation(lang: string): Observable<Record<string, string>> {
      return this.h.get<Record<string, string>>(`/assets/i18n/${lang}.json`);
    }
  })(http);
}

/**
 * Restores the authenticated session at boot by silently refreshing the
 * access token through the HttpOnly refresh cookie. The returned observable
 * is awaited by provideAppInitializer, so the router's initial navigation
 * (and therefore the auth guards) only runs once the session state is final.
 */
export function restoreSessionOnBoot(): Observable<boolean> {
  return inject(AuthService).restoreSession();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideAppInitializer(restoreSessionOnBoot),
    provideHttpClient(
      withInterceptors([JwtInterceptor, HttpErrorInterceptor]),
      withInterceptorsFromDi()
    ),
    importProvidersFrom(MatSnackBarModule),
    provideAnimationsAsync(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'en',
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};
